import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Avatar,
  Button,
} from "@mui/material";
import SportsSoccerRoundedIcon from "@mui/icons-material/SportsSoccerRounded";

function ResultBadge({ value }) {
  const isWin = value === "W";
  const isDraw = value === "D";

  const label = value === "W" ? "G" : value === "D" ? "E" : "P";

  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: ".72rem",
        fontWeight: 900,
        color: "#fff",
        backgroundColor: isWin ? "#15803d" : isDraw ? "#ca8a04" : "#dc2626",
      }}
    >
      {label}
    </Box>
  );
}

function TeamLogo({ src, name }) {
  return (
    <Avatar
      src={src || ""}
      alt={name}
      sx={{
        width: 38,
        height: 38,
        bgcolor: src ? "#fff" : "#e8f5ec",
        border: "1px solid rgba(6,78,42,0.16)",
        boxShadow: "0 6px 14px rgba(6,78,42,0.10)",
      }}
    >
      {!src && (
        <SportsSoccerRoundedIcon
          sx={{
            fontSize: 23,
            color: "#064e2a",
          }}
        />
      )}
    </Avatar>
  );
}

function StandingsSection() {
  const [categories, setCategories] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadStandings = () => {
    fetch("/standings/list/")
      .then((res) => res.json())
      .then((data) => {
        setCategories(data || []);
        setLoading(false);
      })
      .catch(() => {
        setCategories([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadStandings();

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadStandings();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const activeCategory = categories[tabValue];

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 5, md: 8 },
        background:
          "linear-gradient(180deg, #ffffff 0%, #f4fbf6 45%, #e8f5ec 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 360,
          height: 360,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(6,78,42,0.12), transparent 70%)",
          top: -150,
          left: -120,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 420,
          height: 420,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(21,128,61,0.12), transparent 70%)",
          bottom: -190,
          right: -140,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 900,
            fontSize: { xs: "1.8rem", md: "2.45rem" },
            color: "#052e16",
            mb: 1,
          }}
        >
          Tabla de posiciones
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            color: "#3f5f4b",
            fontWeight: 600,
            mb: 4,
          }}
        >
          Liga Deportiva Cantonal Mejía
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress sx={{ color: "#064e2a" }} />
          </Box>
        ) : !categories.length ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "22px",
              border: "1px solid rgba(6,78,42,0.14)",
              py: 4,
              textAlign: "center",
              boxShadow: "0 18px 42px rgba(6,78,42,0.10)",
            }}
          >
            <Typography sx={{ fontWeight: 800, color: "#064e2a" }}>
              No hay tablas de posiciones disponibles
            </Typography>
          </Paper>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                mb: 2.5,
                borderRadius: "18px",
                border: "1px solid rgba(6,78,42,0.14)",
                overflow: "hidden",
                boxShadow: "0 14px 34px rgba(6,78,42,0.09)",
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    fontWeight: 900,
                    color: "#3f5f4b",
                    textTransform: "none",
                  },
                  "& .Mui-selected": {
                    color: "#064e2a !important",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#064e2a",
                    height: 3,
                  },
                }}
              >
                {categories.map((cat) => (
                  <Tab key={cat.category_id} label={cat.category} />
                ))}
              </Tabs>
            </Paper>

            {activeCategory && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "24px",
                  border: "1px solid rgba(6,78,42,0.14)",
                  overflow: "hidden",
                  boxShadow: "0 24px 56px rgba(6,78,42,0.13)",
                }}
              >
                <Box
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: 1.8,
                    background:
                      "linear-gradient(135deg, #064e2a 0%, #15803d 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 900,
                      fontSize: { xs: "1rem", md: "1.15rem" },
                    }}
                  >
                    Categoría {activeCategory.category}
                  </Typography>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#d9edcf" }}>
                        {[
                          "#",
                          "Equipo",
                          "PJ",
                          "PG",
                          "PE",
                          "PP",
                          "GF",
                          "GC",
                          "DG",
                          "PTS",
                          "Forma",
                        ].map((label) => (
                          <TableCell
                            key={label}
                            sx={{
                              fontWeight: 950,
                              color: "#052e16",
                              borderBottom: "1px solid rgba(6,78,42,0.18)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {activeCategory.table.map((row, index) => {
                        const draws = Math.max(
                          Number(row.gp || 0) -
                            Number(row.w || 0) -
                            Number(row.l || 0),
                          0
                        );

                        return (
                          <TableRow
                            key={row.team_id}
                            sx={{
                              backgroundColor:
                                index === 0
                                  ? "rgba(21,128,61,.08)"
                                  : index % 2 === 0
                                  ? "#ffffff"
                                  : "#fbfffc",
                              transition: "background-color .2s ease",
                              "&:hover": {
                                backgroundColor: "rgba(6,78,42,.07)",
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 950,
                                color: index === 0 ? "#15803d" : "#052e16",
                              }}
                            >
                              {row.rank || index + 1}
                            </TableCell>

                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={1.2}
                                alignItems="center"
                                sx={{ minWidth: 180 }}
                              >
                                <TeamLogo src={row.team_logo} name={row.team} />

                                <Typography
                                  sx={{
                                    fontWeight: 900,
                                    color: "#052e16",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {row.team}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700 }}>
                              {row.gp}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700 }}>
                              {row.w}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700 }}>
                              {draws}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700 }}>
                              {row.l}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700 }}>
                              {row.pf}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700 }}>
                              {row.pa}
                            </TableCell>

                            <TableCell
                              sx={{
                                fontWeight: 900,
                                color:
                                  row.diff > 0
                                    ? "#15803d"
                                    : row.diff < 0
                                    ? "#dc2626"
                                    : "#374151",
                              }}
                            >
                              {row.diff > 0 ? `+${row.diff}` : row.diff}
                            </TableCell>

                            <TableCell
                              sx={{
                                fontWeight: 950,
                                color: "#064e2a",
                                fontSize: "1rem",
                              }}
                            >
                              {row.pts}
                            </TableCell>

                            <TableCell>
                              <Stack direction="row" spacing={0.5}>
                                {row.form?.length
                                  ? row.form.map((r, i) => (
                                      <ResultBadge key={i} value={r} />
                                    ))
                                  : "-"}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Button
            variant="contained"
            onClick={() => window.open("/reglamento-futbol", "_blank")}
            sx={{
              borderRadius: "14px",
              fontWeight: 900,
              px: 3.5,
              py: 1.15,
              mt: 4,
              backgroundColor: "#064e2a",
              color: "#fff",
              textTransform: "none",
              boxShadow: "0 14px 30px rgba(6,78,42,0.24)",
              "&:hover": {
                backgroundColor: "#052e16",
                transform: "scale(1.05)",
              },
            }}
          >
            Ver reglamento de fútbol
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default StandingsSection;